import { useMutation, useQueryClient } from "@tanstack/react-query";
import createPrivateAdminPanelInstance from "../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../api/ApiRoutes";

export async function patchAdminCollection(
  code: string,
  body: Record<string, unknown>,
) {
  const token = JSON.parse(localStorage.getItem("token") ?? "null");
  const instance = createPrivateAdminPanelInstance(token?.AccessToken);
  const url = API_ROUTES.PATCH_ADMIN_COLLECTION.replace(
    "{code}",
    encodeURIComponent(code),
  );
  const { data } = await instance.patch(url, body);
  if (data?.success === false) {
    throw new Error(data?.message || "Failed to update collection");
  }
  return data;
}

export function useToggleAdminCollection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ code, is_active }: { code: string; is_active: boolean }) =>
      patchAdminCollection(code, { is_active }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["casinoConfiguration", "collections"],
      });
    },
  });
}

/**
 * Update mutation for the General-tab "Save" path (name, default_view,
 * icon_url, parent_code, is_active). Invalidates BOTH the list cache and
 * the per-collection detail cache so:
 *   - the modal re-renders with the freshly-saved icon / name without a
 *     manual page refresh, and
 *   - the parent list view picks up the new icon / name on its next read.
 *
 * This was previously a raw `patchAdminCollection(...)` call from the
 * modal, which left both caches stale until a hard refresh.
 */
export function useUpdateAdminCollection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      code,
      body,
    }: {
      code: string;
      body: Record<string, unknown>;
    }) => patchAdminCollection(code, body),
    onSuccess: (_data, { code }) => {
      void queryClient.invalidateQueries({
        queryKey: ["casinoConfiguration", "collections"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["casinoConfiguration", "collections", "detail", code],
      });
    },
  });
}

export type CreateAdminCollectionBody = {
  code: string;
  name: string;
  kind: "games" | "providers";
  default_view: string;
  icon_url: string | null;
  parent_code: string | null;
  is_active: boolean;
  sibling_order: number | null;
  /**
   * Whether the live UI should animate the collection's icon. Always
   * sent — even when false — so the BE doesn't have to special-case
   * "field absent" and the persisted record is unambiguous.
   */
  is_animated: boolean;
};

export async function createAdminCollection(body: CreateAdminCollectionBody) {
  const token = JSON.parse(localStorage.getItem("token") ?? "null");
  const instance = createPrivateAdminPanelInstance(token?.AccessToken);
  const { data } = await instance.post(API_ROUTES.POST_ADMIN_COLLECTIONS, body);
  if (data?.success === false) {
    throw new Error(data?.message || "Failed to create collection");
  }
  return data;
}

export function useCreateAdminCollection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAdminCollectionBody) =>
      createAdminCollection(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["casinoConfiguration", "collections"],
      });
    },
  });
}

/**
 * DELETE `/admin-panel-casino/v3/collections/{code}`.
 *
 * Backend contract:
 *   - 204 No Content on success — empty body, nothing to parse.
 *   - 4xx with a JSON body `{ success: false, message }` on validation
 *     failure (subcollections / placements still reference this code, or
 *     attempting to delete a seed collection). We surface that message
 *     verbatim so the admin sees exactly which reference is blocking.
 *   - Anything else falls back to a generic localised error in the caller.
 */
export async function deleteAdminCollection(code: string): Promise<void> {
  const token = JSON.parse(localStorage.getItem("token") ?? "null");
  const instance = createPrivateAdminPanelInstance(token?.AccessToken);
  const url = API_ROUTES.DELETE_ADMIN_COLLECTION.replace(
    "{code}",
    encodeURIComponent(code),
  );
  const { data } = await instance.delete(url);
  // 204 returns no body; only inspect when the BE chose to send one.
  if (data && typeof data === "object" && data.success === false) {
    throw new Error(data?.message || "Failed to delete collection");
  }
}

export function useDeleteAdminCollection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => deleteAdminCollection(code),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["casinoConfiguration", "collections"],
      });
    },
  });
}
